/**
 * 작성자 : 박승호
 * 날짜 : 2025.03.31
 * 경로 : 출역 관리 - 연 출역현황 (사내)
 */
import { useEffect, useState, useRef, Dispatch, SetStateAction } from 'react';
import { PulseLoader } from 'react-spinners';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { IoChevronUpSharp } from 'react-icons/io5';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { pageInfoState, userState } from '../../atoms';
import { BtnGhost } from '../../components/Button';
import { PMS_NORMAL_SUB_CODE, YEAR_LIST } from '../../_constants';
import { SearchOptions } from '../../assets/styles/SearchOptions';
import { scrollToNodeTop } from '../../utils/scrollToNodeTop';
import { useDetectScrolledToBottom } from '../../utils/useDetectScrolledToBottom';
import { FloatingButtonToTop } from '../../assets/styles/FloatingButtonToTop';
import { pad, todayYYYYMMDD } from '../../utils/formatDate';
import Tab from '../../components/Tab';
import Portal from '../../components/Portal';
import AttendListMonthlyModal from '../../components/Modal/AttendListMonthlyModal';
import useOnKeydownF9 from '../../utils/useOnKeydownF9';
import { logPost } from '../../services/log';
import { ISelectBox, ITabObject } from 'customTypes';
import SearchSelectBoxSm from '../../components/SearchSelectBoxSm';
import { useSetAuth } from '../../utils/useSetAuth';
import TuiGrid from '../../components/Table/TuiGrid';
import TuiGridWrapper from '../../components/Table/TuiGridWrapper';
import { useGetFrozenCount } from '../../utils/getFrozenCount';
import { LoadingModalBackground } from '../../assets/styles/Modal';
import { ContentsContainerRoot } from '../../assets/styles/ContentsContainerRoot';
import i18n from '../../translation/i18n';
import { apiGet } from '../../services/_common';
import SelectBox from '../../components/SelectBox';
import ShortcutButton from '../../components/button/ShortcutButton';
import useTuiGridSize from '../../hooks/useTuiGridSize';
import { showExcelButton } from '../../utils/deviceAuth';

const Root = styled(ContentsContainerRoot)`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem 0;
`;

interface IModal {
  status: boolean;
  type: string;
  searchOption: ISearchOption;
}

interface ISearchOption {
  sjName: string;
  sjCd: string;
  aDate: string;
}

const Cattend3 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const size = useOutletContext<any>();
  const tabInfo = useRecoilValue(pageInfoState);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(tabInfo.defaultMrCd);
  const toDay = todayYYYYMMDD();
  const userInfo = useRecoilValue(userState);
  const { userMenuList } = userInfo;
  const [columns, setColumns] = useState<any[]>([]);
  const [summary, setSummary] = useState({});
  const tableRef = useRef<HTMLDivElement>(null);
  const [siteJoinInfoList, setSiteJoinInfoList] = useState<any[]>([]);
  const [orgSiteJoinInfoList, setOrgSiteJoinInfoList] = useState<any[]>([]);
  const [partCd, setPartCd] = useState({ type: 'PART_CD', PART_CD: 'A', cdName: t('전체') });
  const [aYear, setAYear] = useState({ type: 'aYear', aYear: toDay.substring(0, 4), cdName: toDay.substring(0, 4) });
  const [partList, setPartList] = useState<ISelectBox[]>([]);
  const [excelBtn, setExcelBtn] = useState(false);
  const [searchBtn, setSearchBtn] = useState(false);
  const [tableState, setTableState] = useState<any[]>([]);
  const { isBottom } = useDetectScrolledToBottom(tableRef);
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', searchOption: { sjName: '', sjCd: '', aDate: '' } });
  const [newTabList, setNewTabList] = useState<ITabObject[]>();
  const [loading, setLoading] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const frozenCount = useGetFrozenCount();
  const showExcelBtn = showExcelButton(auth.excelAuth);
  const [searchParams] = useSearchParams();
  const tabCd = searchParams.get('tabCd');

  // URL의 tabCd 파라미터 변경 감지하여 탭 업데이트
  useEffect(() => {
    if (tabCd && tabCd !== currentTabMrCd) {
      setCurrentTabMrCd(tabCd);
    }
  }, [tabCd]);

  useEffect(() => {
    const arr: any[] = [];
    userMenuList.map((v: any) => {
      const result = tabInfo?.tabList?.find((el: ITabObject) => el.mtCd === v.mtCd && el.mdCd === v.mdCd && el.mrCd === v.mrCd);
      if (result) {
        arr.push(result);
        setNewTabList(arr);
      }
    });
  }, [tabInfo.tabList]);

  /*
  useEffect(() => {
    getAttendYearAPI().then((res: any) => {
      if (res.status === 200) {
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '연 출역현황(사내)',
          action: '조회',
          etc: ``,
        });
      }
    });
  }, []);
  */

  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    const header = [];
    header.push({ header: t('부서명'), name: 'partName', align: 'left', filter: 'select', width: 200, renderer: { classNames: ['text_secondary'] } });
    header.push({ header: t('전년 누계'), name: 'lastYearCnt', align: 'center', minWidth: 100, renderer: { classNames: ['text_secondary'] } });
    header.push({ header: t('금년 누계'), name: 'yearCnt', align: 'center', minWidth: 100, renderer: { classNames: ['text_secondary'] } });
    for (let i = 0; i < 12; i += 1) {
      header.push({
        header: `${i + 1}${t('-월')}`,
        name: `${aYear.aYear}${pad(i + 1, 2)}`,
        align: 'center',
        minWidth: 60,
        renderer: { classNames: ['text_secondary'] },
      });
    }
    setColumns(header);

    const summaryArr = [];
    for (let i = 0; i < 12; i += 1) {
      summaryArr.push({
        header: `${i + 1}${t('월')}`,
        name: `${aYear.aYear}${pad(i + 1, 2)}`,
        align: 'center',
      });
    }
    const summaryObj: any = {};
    summaryArr.map((v: any) => {
      summaryObj[v.name] = {
        template: (valueMap: any) => {
          return `${valueMap.sum}`;
        },
      };
    });
    setSummary({
      height: 30,
      position: 'top',
      columnContent: {
        partName: t('전체'),
        lastYearCnt: {
          template: (valueMap: any) => {
            return `${valueMap.sum}`;
          },
        },
        yearCnt: {
          template: (valueMap: any) => {
            return `${valueMap.sum}`;
          },
        },
        ...summaryObj,
      },
    });
  }, [searchBtn, i18n.language]);

  // 연 출역현황 조회 API
  const getAttendYearAPI = async () => {
    const req = { cCd: userInfo.hCd, aYear: aYear.aYear, partCd: partCd.PART_CD !== 'A' ? partCd.PART_CD : '' };
    const res = await apiGet({ path: '/attend/year/company', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      setLoading(false);
      setTableState(
        data.attendList.map((v: any) => {
          return {
            partName: v.partName,
            ...v.month,
            lastYearCnt: v.lastYearCnt,
            yearCnt: v.yearCnt,
          };
        })
      );
    } else {
      setLoading(false);
      // toast.error(t(ERROR));
    }
  };

  // 부서명, 직급명 조회
  const setPmsNormalSubList = async (subCd: string, setState: Dispatch<SetStateAction<ISelectBox[]>>) => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, subCd };
    const res = await apiGet({ path: '/code/pmsNormalTitle', req });
    const { status } = res;
    const cdFind: string = PMS_NORMAL_SUB_CODE.find((el) => el.code.includes(subCd))?.type || '';
    if (status === 200) {
      const defaultObject = { type: cdFind, [cdFind]: 'A', cdName: t('전체') };
      const newArr = res.data.data.pmsNormalTitleList?.map((el: ISelectBox) => ({ type: cdFind, [cdFind]: el.subCd, cdName: el.cdName }));
      setState([defaultObject, ...newArr]);
    }
  };

  useEffect(() => {
    setPmsNormalSubList('L', setPartList);
  }, []);

  // 검색 버튼 클릭
  const onClickSearch = () => {
    if (!openModal.status) {
      setLoading(true);
      navigate({
        pathname: location.pathname,
        search: '',
      });
      getAttendYearAPI();
      setSearchBtn(!searchBtn);
    }
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    if (tableState.length <= 0) return;
    setExcelBtn(true);
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '연 출역현황',
      action: '엑셀 저장',
      etc: ``,
    });
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  // 인쇄 버튼 클릭
  const onClickPrint = () => {
    handlePrint();
  };

  // Row 클릭
  const onClickRow = (rowData: any, columnName: string) => {
    if (rowData === 'summary' && columnName !== 'yearCnt' && columnName !== 'lastYearCnt' && columnName !== 'partName' && columnName !== '_number') {
      const data = { partName: '전체', partCd: '', sjName: '', sjCd: '', aDate: columnName };
      setOpenModal({ status: true, type: 'monthlyAttendList', searchOption: data });
      return;
    }
    if (columnName !== 'yearCnt' && columnName !== 'lastYearCnt' && columnName !== 'partName' && columnName !== '_number' && rowData[columnName] !== 0) {
      const partObject = partList.find((partItem) => partItem.cdName === rowData.partName);
      // const sjObject = orgSiteJoinInfoList.find((el) => el.sjName === rowData.sjName);
      /**
       * 수정자 : 한영광
       * 수정일자 : 2023.10.20
       * 수정내용 : 협력업체가 없는 출역데이터 예외처리 -> 로컬에서 출역데이터 전달 시 협력업체 없이 전달될 수 있음
       */
      const data = { partName: partObject?.cdName, partCd: partObject ? partObject?.PART_CD : '', sjName: '', sjCd: '', aDate: columnName };
      setOpenModal({ status: true, type: 'monthlyAttendList', searchOption: data });
      // if (sjObject) {
      //   const data = { sjName: rowData.sjName, sjCd: sjObject.sjCd, aDate: columnName };
      //   setOpenModal({ status: true, type: 'monthlyAttendList', searchOption: data });
      //   // navigate('/s_attend2', { state: data });
      // }
    }
  };

  const renderExcelBtn = () => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickExcelSave}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
          {/* <span className='material-symbols-rounded'>download</span>저장 */}
        </BtnGhost>
      );
    }
    return null;
  };

  const renderPrintBtn = () => {
    if (auth.printAuth) {
      return (
        <BtnGhost onClick={onClickPrint}>
          <span className='material-symbols-rounded'>print</span>
          {t('인쇄')}
        </BtnGhost>
      );
    }
    return null;
  };

  const onClickTab = (tab: string) => {
    setCurrentTabMrCd(tab);
  };

  return (
    <div className='contents'>
      <div className='content-container oneColumn'>
        <Tab tabList={newTabList || tabInfo.tabList} currentTabMrCd={currentTabMrCd} setCurrentTabMrCd={setCurrentTabMrCd} onClickTab={onClickTab} size={size} />
        <Root loading={loading}>
          <SearchOptions>
            {isBottom && (
              <FloatingButtonToTop>
                <button type='button' onClick={() => scrollToNodeTop(tableRef)}>
                  <IoChevronUpSharp size={20} style={{ stroke: 'white' }} />
                </button>
              </FloatingButtonToTop>
            )}
            <div className='inputsWrapper'>
              <div className='inputForm-row withLabel'>
                <label htmlFor='partCd'>{t('부서명')}</label>
                <div className='inputForm-col'>
                  <SelectBox options={partList} defaultOption={t('전체')} state={partCd} setState={setPartCd} stateKey='PART_CD' initiateKey={partCd.PART_CD} filterbar='filter-1-left' />
                </div>
              </div>
              <div className='inputForm-row'>
                <div className='inputForm-col'>
                  <SelectBox options={YEAR_LIST} defaultOption={aYear.aYear} state={aYear} setState={setAYear} stateKey='aYear' filterbar='filter-1-left' optionHeight='height-md' />
                </div>
              </div>
            </div>
            <div className='inputsWrapper'>
              <div className='secondSearchOption'>
                <div className='flex-basic textBtnGroup'>
                  <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                </div>
                <div className='flex-basic iconBtnGroup'>
                  {renderExcelBtn()}
                  {/* {renderPrintBtn()} */}
                </div>
              </div>
            </div>
          </SearchOptions>
          <TuiGridWrapper componentRef={componentRef}>
            <TuiGrid
              data={tableState}
              columns={columns}
              perPage={5}
              excelBtn={excelBtn}
              setExcelBtn={setExcelBtn}
              onClickRow={onClickRow}
              summary={summary}
              height={tuiHeight}
              frozenCount={frozenCount}
            />
          </TuiGridWrapper>
        </Root>
      </div>
      {loading && (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'monthlyAttendList' && <AttendListMonthlyModal setOpenModal={setOpenModal} filter={openModal.searchOption} dGubun='c' />}
      </Portal>
    </div>
  );
};

export default Cattend3;
